export const STATUS_META = {
  operational: { label: "Operational", color: "#067647", bg: "#DCFAE6" },
  degraded: { label: "Degraded", color: "#B54708", bg: "#FEF0C7" },
  down: { label: "Down", color: "#B42318", bg: "#FEE4E2" },
  unknown: { label: "Unknown", color: "#475467", bg: "#EAECF0" },
}

export function statusMeta(status) {
  return STATUS_META[status] || STATUS_META.unknown
}

// The banner must never read "running normally" when AWS data was unreadable.
export function bannerMessage(snapshot) {
  if (!snapshot) return "Checking status…"
  const { overall, data_complete: dataComplete } = snapshot
  if (overall === "down") return "OneBase has an outage"
  if (overall === "degraded") return "Parts of OneBase are slow or degraded"
  if (overall === "operational") {
    return dataComplete === false
      ? "No problems detected, but some AWS data could not be read"
      : "OneBase is running normally"
  }
  return "OneBase status is unknown"
}

export function bannerStatus(snapshot) {
  if (!snapshot) return "unknown"
  if (snapshot.overall === "operational" && snapshot.data_complete === false) {
    return "degraded"
  }
  return snapshot.overall
}

export function formatMs(value) {
  if (value === null || value === undefined) return "–"
  return value >= 1000
    ? `${(value / 1000).toFixed(1)} s`
    : `${Math.round(value)} ms`
}

export function formatPercent(rate) {
  if (rate === null || rate === undefined) return "–"
  return `${(rate * 100).toFixed(1)}%`
}

export function formatNumber(value) {
  if (value === null || value === undefined) return "–"
  return Math.round(value).toLocaleString("en-US")
}

export function formatPercentValue(value) {
  if (value === null || value === undefined) return "–"
  return `${Math.round(value * 10) / 10}%`
}

// Chart points as {t, v}, with the value scaled (e.g. seconds to milliseconds).
export function seriesPoints(series, key, scale = 1) {
  return (series?.[key] || []).map((point) => ({
    t: point.t,
    v: point.v * scale,
  }))
}

export function seriesValues(series, key, scale = 1) {
  return (series?.[key] || []).map((point) => point.v * scale)
}

export function seriesPeak(values) {
  return values.length ? Math.max(...values) : null
}

export function formatDateTime(iso) {
  if (!iso) return "–"
  return new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

// Beanstalk's own colours (Green / Yellow / Red / Grey) onto our status styling.
const EB_COLOR_TO_STATUS = {
  Green: "operational",
  Yellow: "degraded",
  Red: "down",
  Grey: "unknown",
}

export function healthColorMeta(color) {
  const status = EB_COLOR_TO_STATUS[color] || "unknown"
  return { ...statusMeta(status), status, colorName: color || "Grey" }
}

// Beanstalk health names (instance counts, health transitions) onto statuses.
const HEALTH_NAME_TO_STATUS = {
  Ok: "operational",
  Info: "operational",
  Warning: "degraded",
  Degraded: "degraded",
  Severe: "down",
}

export function healthNameMeta(name) {
  return statusMeta(HEALTH_NAME_TO_STATUS[name] || "unknown")
}

const DEPLOY_STATUS_TO_STATUS = {
  Succeeded: "operational",
  InProgress: "degraded",
  Stopping: "degraded",
  Failed: "down",
  Stopped: "down",
  Superseded: "unknown",
}

export function deployStatusMeta(status) {
  return statusMeta(DEPLOY_STATUS_TO_STATUS[status] || "unknown")
}

export function formatDuration(startIso, endIso) {
  if (!startIso || !endIso) return null
  const seconds = Math.round((new Date(endIso) - new Date(startIso)) / 1000)
  if (!Number.isFinite(seconds) || seconds < 0) return null
  if (seconds < 60) return `${seconds} s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  return `${hours} h ${minutes % 60} min`
}

// Newest first, so the latest health change is always at the top.
export function sortWarnings(warnings) {
  return [...(warnings || [])].sort((a, b) =>
    String(b.time).localeCompare(String(a.time))
  )
}
